import { Select, MenuItem, InputLabel, FormControl } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import {
  listingType,
  listingState,
  commercialListingType,
  residentialListingType,
} from "../../../../../@types/listings/unions";
import {
  transactionType,
  addressDisplayMode,
  purposeType as purposeTypes,
  type PurposeType
} from "../../../../../@types/sharedSchema";
import { parkingSpots } from "../../../../../@types/sharedSchema";
import { emptyMenuItem } from "./ListingDialogInputFloors";
import { CheckedMenuItemLabel } from "components/__haim/Common/CheckedMenuItemLabel";
import type { ChangeEvent } from "react";
import type { Listing } from "../../../../../@types/listings/listing";

type ListingSelectsType = keyof Pick<
  Listing,
  | "purposeType"
  | "listingType"
  | "listingState"
  | "transactionType"
  | "parkingSpotsCount"
  | "addressDisplayMode"
>;

type ListingSelectsValue = Listing[ListingSelectsType];

type ListingDialogInputSelectsProps = {
  name: ListingSelectsType;
  value: ListingSelectsValue;
  loading: boolean;
  required?: boolean;
  purposeType?: PurposeType;
  handleChange: ({ target: { name, value } }: ChangeEvent<HTMLInputElement>) => void;
};

export function ListingDialogInputSelects({
  name,
  value,
  loading,
  required,
  purposeType,
  handleChange
}: ListingDialogInputSelectsProps): JSX.Element {
  const { t: translate } = useTranslation();

  let menuItems: JSX.Element[];

  const handleGetLabel = (labelValue: string | number): string | number => {
    let translatedLabel: string | number = labelValue;

    if (name === "purposeType") {
      translatedLabel = translate(`listingsPage.purposeTypeLabel.${labelValue}`) as string;
    }

    if (name === "transactionType") {
      translatedLabel = translate(`listingsPage.transactionTypeLabel.${labelValue}`) as string;
    }

    if (name === "addressDisplayMode") {
      translatedLabel = translate(`listingsPage.addressDisplayModeLabel.${labelValue}`) as string;
    }

    if (name === "listingType") {
      translatedLabel = translate(`listingsPage.listingTypeLabel.${labelValue}`) as string;
    }

    if (name === "listingState") {
      translatedLabel = translate(`listingsPage.listingStateLabel.${labelValue}`) as string;
    }

    return translatedLabel;
  };

  if (name === "purposeType") {
    menuItems = purposeTypes.map((item) => (
      <MenuItem value={item} key={item}>
        <CheckedMenuItemLabel selected={value === item}>
          {handleGetLabel(item)}
        </CheckedMenuItemLabel>
      </MenuItem>
    ));
  }

  if (name === "transactionType") {
    menuItems = transactionType.map((item) => (
      <MenuItem value={item} key={item}>
        <CheckedMenuItemLabel selected={value === item}>
          {translate(`listingsPage.transactionTypeLabel.${item}`)}
        </CheckedMenuItemLabel>
      </MenuItem>
    ));
  }

  if (name === "addressDisplayMode") {
    menuItems = addressDisplayMode.map((item) => (
      <MenuItem value={item} key={item}>
        <CheckedMenuItemLabel selected={value === item}>
          {handleGetLabel(item)}
        </CheckedMenuItemLabel>
      </MenuItem>
    ));
  }

  if (name === "listingType") {
    let parsedListingType: Readonly<string[]> = listingType;

    if (purposeType === "commercial") parsedListingType = commercialListingType;
    if (purposeType === "residential") parsedListingType = residentialListingType;

    menuItems = parsedListingType.map((item) => (
      <MenuItem value={item} key={item}>
        <CheckedMenuItemLabel selected={value === item}>
          {handleGetLabel(item)}
        </CheckedMenuItemLabel>
      </MenuItem>
    ));
  }

  if (name === "listingState") {
    menuItems = listingState.map((item) => (
      <MenuItem value={item} key={item}>
        <CheckedMenuItemLabel selected={value === item}>
          {handleGetLabel(item)}
        </CheckedMenuItemLabel>
      </MenuItem>
    ));
  }

  if (name === "parkingSpotsCount") {
    menuItems = parkingSpots.map((item) => (
      <MenuItem value={item} key={item}>
        <CheckedMenuItemLabel selected={value === item}>{item}</CheckedMenuItemLabel>
      </MenuItem>
    ));
  }

  return (
    <FormControl sx={{ minWidth: 140 }} disabled={loading} fullWidth>
      <InputLabel required={required}>{translate(`listingsPage.tableColumns.${name}`)}</InputLabel>
      <Select
        name={name}
        label={translate(`listingsPage.tableColumns.${name}`)}
        value={value ?? ""}
        required={required}
        onChange={handleChange}
        renderValue={handleGetLabel}
      >
        {emptyMenuItem}
        {menuItems}
      </Select>
    </FormControl>
  );
}
